// Profil utilisateur Fan Zone - Modification pseudo et stats
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  ArrowLeft, User, Trophy, Star, Edit3, Check, X, 
  Gamepad2, Target, Puzzle, MessageSquare, Loader2,
  TrendingUp, Award, Calendar
} from 'lucide-react';
import { db } from '../../firebase';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { LEVELS_CONFIG, calculateLevel, GAMES_CONFIG } from './services/fanZoneConfig';
import { getUserProfile, getUserRank } from './services/fanZoneService';
import logger from '../../utils/logger';
import toast from 'react-hot-toast';

// Scrollbar styles
const scrollbarStyles = `
  .fanzone-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 228, 0, 0.3) transparent;
  }
  .fanzone-scrollbar::-webkit-scrollbar { width: 6px; }
  .fanzone-scrollbar::-webkit-scrollbar-track { background: transparent; }
  .fanzone-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, rgba(255, 228, 0, 0.4) 0%, rgba(189, 255, 0, 0.2) 100%);
    border-radius: 3px;
  }
`;

// Avatars disponibles (emojis)
const AVATARS = ['⚽', '🏆', '🎯', '🦁', '🔥', '⭐', '👑', '💪', '🎮', '🏅'];

// Couleurs de profil disponibles
const PROFILE_COLORS = [
  { id: 'yellow', color: '#FFE400', name: 'Jaune EFAFC' },
  { id: 'lime', color: '#BDFF00', name: 'Vert Lime' },
  { id: 'blue', color: '#3B82F6', name: 'Bleu' },
  { id: 'purple', color: '#8B5CF6', name: 'Violet' },
  { id: 'red', color: '#EF4444', name: 'Rouge' },
  { id: 'orange', color: '#F97316', name: 'Orange' },
];

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div 
    className="p-4 rounded-xl bg-white/5 border border-white/10"
    style={{ borderColor: `${color}30` }}
  >
    <div className="flex items-center gap-3">
      <div 
        className="w-10 h-10 rounded-lg flex items-center justify-center"
        style={{ background: `${color}20` }}
      >
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div>
        <p className="text-gray-400 text-sm">{label}</p>
        <p className="text-white font-bold text-lg">{value}</p>
      </div>
    </div>
  </div>
);

const FanZoneProfile = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [rank, setRank] = useState({ rank: 0, totalPlayers: 0 });
  
  // États d'édition
  const [isEditingPseudo, setIsEditingPseudo] = useState(false);
  const [newPseudo, setNewPseudo] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState('⚽');
  const [selectedColor, setSelectedColor] = useState('yellow');

  // Injecter styles
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.textContent = scrollbarStyles;
    document.head.appendChild(styleEl);
    document.documentElement.classList.add('fanzone-scrollbar');
    document.body.classList.add('fanzone-scrollbar');
    return () => {
      document.head.removeChild(styleEl);
      document.documentElement.classList.remove('fanzone-scrollbar');
      document.body.classList.remove('fanzone-scrollbar');
    };
  }, []);

  // Charger le profil
  useEffect(() => {
    const loadProfile = async () => {
      if (!currentUser) {
        navigate('/auth');
        return;
      }

      try {
        setLoading(true);
        
        const displayName = currentUser.displayName || currentUser.email?.split('@')[0] || 'Fan EFAFC';
        
        // Essayer de charger depuis Firebase
        let userProfile = null;
        try {
          userProfile = await getUserProfile(currentUser.uid);
        } catch (e) {
          logger.warn('Permission Firebase ignorée');
        }
        
        if (userProfile) {
          setProfile(userProfile);
          setNewPseudo(userProfile.pseudo || displayName);
          setSelectedAvatar(userProfile.avatar || '⚽');
          setSelectedColor(userProfile.profileColor || 'yellow');
        } else {
          // Profil par défaut
          const levelInfo = calculateLevel(0);
          const defaultProfile = {
            id: currentUser.uid,
            pseudo: displayName,
            avatar: '⚽',
            profileColor: 'yellow',
            totalXP: 0,
            level: 1,
            levelInfo,
            gamesPlayed: { pronostics: 0, quiEstCe: 0, wordle: 0, puzzle: 0 },
            createdAt: new Date()
          };
          setProfile(defaultProfile);
          setNewPseudo(displayName);
        }
        
        // Charger le rang
        try {
          const userRank = await getUserRank(currentUser.uid);
          setRank(userRank);
        } catch (e) {
          setRank({ rank: 1, totalPlayers: 1 });
        }
        
      } catch (error) {
        logger.error('Erreur chargement profil:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [currentUser, navigate]);

  const handleSavePseudo = async () => {
    if (!newPseudo.trim() || newPseudo.length < 2) {
      toast.error('Le pseudo doit avoir au moins 2 caractères');
      return;
    }
    
    if (newPseudo.length > 20) {
      toast.error('Le pseudo ne peut pas dépasser 20 caractères');
      return;
    }
    
    try {
      setSaving(true);
      
      // Mettre à jour Firebase
      try {
        const userRef = doc(db, 'fanzone_users', currentUser.uid);
        await setDoc(userRef, { 
          pseudo: newPseudo.trim(),
          updatedAt: new Date()
        }, { merge: true });
      } catch (e) {
        logger.warn('Sauvegarde Firebase ignorée, stockage local');
      }
      
      // Mettre à jour l'état local
      setProfile(prev => ({ ...prev, pseudo: newPseudo.trim() }));
      setIsEditingPseudo(false);
      
      toast.success('Pseudo mis à jour !');
    } catch (error) {
      logger.error('Erreur sauvegarde pseudo:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAvatar = async (avatar) => {
    try {
      setSelectedAvatar(avatar);
      
      // Sauvegarder dans Firebase
      try {
        const userRef = doc(db, 'fanzone_users', currentUser.uid);
        await setDoc(userRef, { 
          avatar,
          updatedAt: new Date()
        }, { merge: true });
      } catch (e) {
        logger.warn('Sauvegarde avatar ignorée');
      }
      
      setProfile(prev => ({ ...prev, avatar }));
      toast.success('Avatar mis à jour !');
    } catch (error) {
      logger.error('Erreur sauvegarde avatar:', error);
    }
  };

  const handleSaveColor = async (colorId) => {
    try {
      setSelectedColor(colorId);
      
      // Sauvegarder dans Firebase
      try {
        const userRef = doc(db, 'fanzone_users', currentUser.uid);
        await setDoc(userRef, { 
          profileColor: colorId,
          updatedAt: new Date()
        }, { merge: true });
      } catch (e) {
        logger.warn('Sauvegarde couleur ignorée');
      }
      
      setProfile(prev => ({ ...prev, profileColor: colorId }));
      toast.success('Couleur mise à jour !');
    } catch (error) {
      logger.error('Erreur sauvegarde couleur:', error);
    }
  };

  const getProfileColor = () => {
    const colorObj = PROFILE_COLORS.find(c => c.id === selectedColor);
    return colorObj?.color || '#FFE400';
  };

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #0a0f1a 0%, #0d1526 50%, #050a12 100%)' }}
      >
        <Loader2 className="w-8 h-8 text-[#FFE400] animate-spin" />
      </div>
    );
  }

  const levelInfo = profile?.levelInfo || calculateLevel(profile?.totalXP || 0);
  const profileColor = getProfileColor();
  const totalGames = Object.values(profile?.gamesPlayed || {}).reduce((a, b) => a + b, 0);

  return (
    <div 
      className="min-h-screen pb-8"
      style={{ background: 'linear-gradient(135deg, #0a0f1a 0%, #0d1526 50%, #050a12 100%)' }}
    >
      <div className="max-w-lg mx-auto px-4 pt-6">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-6"
        >
          <button
            onClick={() => navigate('/fanzone/dashboard')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour</span>
          </button>
          
          <h1 className="text-xl font-bold text-white">Mon Profil</h1>
          
          <div className="w-20" /> {/* Spacer */}
        </motion.div>

        {/* Avatar & Pseudo Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div 
            className="p-6 rounded-2xl"
            style={{
              background: `linear-gradient(135deg, ${profileColor}15 0%, ${profileColor}05 100%)`,
              border: `1px solid ${profileColor}30`
            }}
          >
            {/* Avatar grand */}
            <div className="flex flex-col items-center mb-6">
              <div 
                className="w-24 h-24 rounded-full flex items-center justify-center text-5xl mb-4"
                style={{
                  background: `linear-gradient(135deg, ${profileColor}40, ${profileColor}20)`,
                  border: `3px solid ${profileColor}`,
                  boxShadow: `0 0 30px ${profileColor}30`
                }}
              >
                {selectedAvatar}
              </div>
              
              {/* Pseudo */}
              <div className="flex items-center gap-2">
                {isEditingPseudo ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newPseudo}
                      onChange={(e) => setNewPseudo(e.target.value)}
                      maxLength={20}
                      className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-center focus:outline-none focus:border-[#FFE400]/50"
                      autoFocus
                    />
                    <button
                      onClick={handleSavePseudo}
                      disabled={saving}
                      className="p-2 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors"
                    >
                      {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingPseudo(false);
                        setNewPseudo(profile?.pseudo || '');
                      }}
                      className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold text-white">{profile?.pseudo || 'Fan EFAFC'}</h2>
                    <button
                      onClick={() => setIsEditingPseudo(true)}
                      className="p-2 rounded-lg bg-white/10 text-gray-400 hover:text-white hover:bg-white/20 transition-colors"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
              
              {/* Niveau */}
              <div className="flex items-center gap-2 mt-2">
                <span 
                  className="px-3 py-1 rounded-full text-sm font-medium"
                  style={{ background: `${levelInfo.color}30`, color: levelInfo.color }}
                >
                  Niv. {levelInfo.level} - {levelInfo.title}
                </span>
              </div>
            </div>

            {/* Sélection Avatar */}
            <div className="mb-6">
              <p className="text-gray-400 text-sm mb-3 text-center">Choisis ton avatar</p>
              <div className="flex flex-wrap justify-center gap-2">
                {AVATARS.map(avatar => (
                  <button
                    key={avatar}
                    onClick={() => handleSaveAvatar(avatar)}
                    className={`w-12 h-12 rounded-xl text-2xl transition-all ${
                      selectedAvatar === avatar 
                        ? 'bg-white/20 scale-110 ring-2 ring-[#FFE400]' 
                        : 'bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    {avatar}
                  </button>
                ))}
              </div>
            </div>

            {/* Sélection Couleur */}
            <div>
              <p className="text-gray-400 text-sm mb-3 text-center">Couleur de profil</p>
              <div className="flex justify-center gap-3">
                {PROFILE_COLORS.map(color => (
                  <button
                    key={color.id}
                    onClick={() => handleSaveColor(color.id)}
                    className={`w-10 h-10 rounded-full transition-all ${
                      selectedColor === color.id ? 'scale-125 ring-2 ring-white' : 'hover:scale-110'
                    }`}
                    style={{ background: color.color }}
                    title={color.name}
                  />
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#FFE400]" />
            Statistiques
          </h3>
          
          <div className="grid grid-cols-2 gap-3">
            <StatCard 
              icon={Star} 
              label="XP Total" 
              value={profile?.totalXP?.toLocaleString() || 0}
              color="#FFE400"
            />
            <StatCard 
              icon={Trophy} 
              label="Classement" 
              value={`#${rank.rank} / ${rank.totalPlayers}`}
              color="#BDFF00"
            />
            <StatCard 
              icon={Gamepad2} 
              label="Parties jouées" 
              value={totalGames}
              color="#3B82F6"
            />
            <StatCard 
              icon={Award} 
              label="Niveau" 
              value={levelInfo.level}
              color={levelInfo.color}
            />
          </div>
        </motion.div>

        {/* Progression XP */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-6"
        >
          <div className="p-4 rounded-xl bg-white/5 border border-white/10">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-400 text-sm">Progression vers niveau {(levelInfo.level || 1) + 1}</span>
              <span className="text-[#FFE400] font-bold">{levelInfo.xpForNext || 500} XP restants</span>
            </div>
            <div className="h-3 bg-gray-700 rounded-full overflow-hidden">
              <motion.div 
                initial={{ width: 0 }}
                animate={{ width: `${levelInfo.progressToNext || 0}%` }}
                transition={{ duration: 1, ease: "easeOut" }}
                className="h-full rounded-full"
                style={{ background: `linear-gradient(90deg, #FFE400, #BDFF00)` }}
              />
            </div>
          </div>
        </motion.div>

        {/* Détail par jeu */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Gamepad2 className="w-5 h-5 text-[#BDFF00]" />
            Parties par jeu
          </h3>
          
          <div className="space-y-3">
            {Object.entries(GAMES_CONFIG).map(([gameId, game]) => {
              const played = profile?.gamesPlayed?.[gameId] || 0;
              const icons = {
                pronostics: Target,
                quiEstCe: User,
                wordle: MessageSquare,
                puzzle: Puzzle
              };
              const GameIcon = icons[gameId] || Gamepad2;
              
              return (
                <div 
                  key={gameId}
                  className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-xl">
                      {game.icon}
                    </div>
                    <div>
                      <p className="text-white font-medium">{game.name}</p>
                      <p className="text-gray-500 text-xs">{game.frequency === 'daily' ? 'Quotidien' : 'Par match'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[#FFE400] font-bold">{played}</p>
                    <p className="text-gray-500 text-xs">parties</p>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Info compte */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-6 pt-6 border-t border-white/10"
        >
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <Calendar className="w-4 h-4" />
            <span>Compte : {currentUser?.email}</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FanZoneProfile;
